class ProductSearchAndSelect extends HTMLElement {

    constructor() {
        super();

        this.productData = [];
        this.filteredProducts = [];
        this.selectedProducts = new Map();

        // Fast product lookup by SKU
        this.productMap = new Map();

        this.currentPage = 0;
        this.batchSize = 100;

        this.searchTerm = '';
        this.isLoading = false;
        this.isOpen = false;

        // Mobile keyboard handling
        this.keyboardHeight = 0;
    }

    connectedCallback() {
        this.init();
    }

    async init() {
        this.render();
        this.cacheElements();
        this.bindEvents();
        await this.loadProducts();
        this.renderProductList();
    }

    /* ========================================
       RENDER MAIN UI
    ======================================== */

    render() {
        this.innerHTML = `
            <div class="nkl__product__model__overlay" data-overlay>
                <div class="nkl__product__model__modal" data-modal role="dialog" aria-modal="true">

                    <!-- HEADER -->
                    <div class="nkl__product__model__header">
                        <div class="nkl__product__model__search-box">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <circle cx="11" cy="11" r="7"></circle>
                                <path d="m20 20-4-4"></path>
                            </svg>

                            <input
                                type="text"
                                class="nkl__product__model__search-input"
                                placeholder="Search product model..."
                            />

                            <a
                                href="javascript:void(0)"
                                type="button"
                                class="nkl__product__model__search-clear"
                                data-action="search-clear"
                                aria-label="Clear search"
                            >&times;</a>
                        </div>

                        <button
                            type="button"
                            class="nkl__product__model__close"
                            data-action="close"
                            aria-label="Close"
                        >&times;</button>
                    </div>

                    <!-- BODY -->
                    <div class="nkl__product__model__body" data-product-list></div>

                    <!-- FOOTER -->
                    <div class="nkl__product__model__footer">
                        <div class="nkl__product__model__selected-wrapper">
                            <div class="nkl__product__model__selected-count" style="display:none">
                                <span data-selected-count>0</span>
                                <span>selected</span>
                            </div>

                            <!-- SELECTED PRODUCT NAMES -->
                            <div class="nkl__product__model__selected-products" data-selected-products></div>
                        </div>

                        <div class="nkl__product__model__footer-actions">
                            <button type="button" class="nkl__product__model__cancel" data-action="close">
                                Cancel
                            </button>

                            <button type="button" class="nkl__product__model__add" data-action="add" disabled>
                                Add Product
                            </button>
                        </div>
                    </div>

                </div>
            </div>
        `;
    }

    /* ========================================
       CACHE DOM ELEMENTS
    ======================================== */

    cacheElements() {
        this.overlay = this.querySelector('[data-overlay]');
        this.modal = this.querySelector('[data-modal]');
        this.productList = this.querySelector('[data-product-list]');
        this.searchInput = this.querySelector('.nkl__product__model__search-input');
        this.selectedCount = this.querySelector('[data-selected-count]');
        this.selectedProductsContainer = this.querySelector('[data-selected-products]');
        this.addButton = this.querySelector('[data-action="add"]');
        this.clearButton = this.querySelector('.nkl__product__model__search-clear');
        this.footer = this.querySelector('.nkl__product__model__footer');
    }

    /* ========================================
       BIND EVENTS
    ======================================== */

    bindEvents() {

        /** ACTION BUTTONS */
        this.addEventListener('click', (event) => {

            const actionElement = event.target.closest('[data-action]');

            if (!actionElement) {
                return;
            }

            const action = actionElement.dataset.action;

            if (action === 'close') {
                this.close();
            }

            if (action === 'add') {
                this.addSelectedProducts();
            }

            if (action === 'search-clear') {
                this.clearSearch();
            }
        });

        /** BACKDROP CLICK */
        this.overlay.addEventListener('click', (event) => {
            if (event.target === this.overlay) {
                this.close();
            }
        });

        /** EXTERNAL OPEN TRIGGER */
        document.addEventListener('nkl:product-model-search:open', () => {
            this.open();
        });

        /** SEARCH */
        this.searchInput.addEventListener('input', () => {

            this.searchTerm = this.searchInput.value.trim().toLowerCase();

            this.updateClearButtonVisibility();

            this.currentPage = 0;

            this.renderProductList();
        });

        /** PRODUCT SELECTION */
        this.productList.addEventListener('change', (event) => {

            if (!event.target.matches('.nkl__product__model__checkbox')) {
                return;
            }

            const sku = event.target.dataset.sku;
            const product = this.productMap.get(sku);

            if (!product) {
                return;
            }

            if (event.target.checked) {
                this.selectedProducts.set(product.sku, product);
            } else {
                this.selectedProducts.delete(product.sku);
            }

            /**
             * Footer + selected names update.
             * Full list re-render nahi karna (performance).
             */
            this.updateSelectedState();
        });

        /** INFINITE SCROLL */
        this.productList.addEventListener('scroll', () => {

            const scrollTop = this.productList.scrollTop;
            const scrollHeight = this.productList.scrollHeight;
            const clientHeight = this.productList.clientHeight;

            const nearBottom = scrollTop + clientHeight >= scrollHeight - 150;

            if (nearBottom) {
                this.loadNextBatch();
            }
        });

        /** ESCAPE KEY */
        document.addEventListener('keydown', (event) => {
            if (event.key === 'Escape' && this.isOpen) {
                this.close();
            }
        });

        /** MOBILE KEYBOARD */
        if (window.visualViewport) {

            window.visualViewport.addEventListener('resize', () => {
                this.updateKeyboardPosition();
            });

            window.visualViewport.addEventListener('scroll', () => {
                this.updateKeyboardPosition();
            });
        }

        /** Selected products drag */
        this.initSelectedProductsDrag();
    }

    /* ========================================
       SELECTED PRODUCTS DRAG
    ======================================== */

    initSelectedProductsDrag() {

        if (!this.selectedProductsContainer) {
            return;
        }

        const slider = this.selectedProductsContainer;

        let isDown = false;
        let startX = 0;
        let scrollLeft = 0;

        /** Mouse */
        slider.addEventListener('mousedown', (event) => {
            isDown = true;
            slider.classList.add('is-dragging');
            startX = event.pageX - slider.offsetLeft;
            scrollLeft = slider.scrollLeft;
        });

        slider.addEventListener('mouseleave', () => {
            isDown = false;
            slider.classList.remove('is-dragging');
        });

        slider.addEventListener('mouseup', () => {
            isDown = false;
            slider.classList.remove('is-dragging');
        });

        slider.addEventListener('mousemove', (event) => {

            if (!isDown) {
                return;
            }

            event.preventDefault();

            const x = event.pageX - slider.offsetLeft;
            const walk = (x - startX) * 1.5;

            slider.scrollLeft = scrollLeft - walk;
        });

        /** Touch */
        let touchStartX = 0;
        let touchScrollLeft = 0;

        slider.addEventListener('touchstart', (event) => {
            touchStartX = event.touches[0].pageX;
            touchScrollLeft = slider.scrollLeft;
        }, { passive: true });

        slider.addEventListener('touchmove', (event) => {
            const touchX = event.touches[0].pageX;
            const walk = touchX - touchStartX;
            slider.scrollLeft = touchScrollLeft - walk;
        }, { passive: true });

        /** Mouse wheel → horizontal */
        slider.addEventListener('wheel', (event) => {

            if (Math.abs(event.deltaY) > Math.abs(event.deltaX)) {
                event.preventDefault();
                slider.scrollLeft += event.deltaY;
            }

        }, { passive: false });
    }

    /* ========================================
       LOAD PRODUCT API DATA
    ======================================== */

    async loadProducts() {

        try {

            this.isLoading = true;

            const response = await fetch(window.warrantyProductModelsUrl);

            if (!response.ok) {
                throw new Error(`Failed to fetch products: ${response.status}`);
            }

            this.productData = await response.json();

            if (!Array.isArray(this.productData)) {
                this.productData = [];
            }

            /** SKU => Product */
            this.productMap = new Map(
                this.productData.map(product => [product.sku, product])
            );

        } catch (error) {

            console.error('Error loading product models:', error);

            this.productData = [];
            this.productMap.clear();

        } finally {

            this.isLoading = false;
        }
    }

    /* ========================================
       OPEN MODAL / DRAWER
    ======================================== */

    open() {

        this.isOpen = true;

        this.overlay.classList.add('is-active');

        document.body.classList.add('nkl__product__model__no-scroll');

        this.currentPage = 0;
        this.searchTerm = '';
        this.searchInput.value = '';

        this.updateClearButtonVisibility();
        this.renderProductList();

        /** Focus search */
        this.searchInput.focus();

        requestAnimationFrame(() => {
            this.searchInput.focus();
            this.updateKeyboardPosition();
        });

        setTimeout(() => {
            this.searchInput.focus();
            this.updateKeyboardPosition();
        }, 260);

        setTimeout(() => {
            this.updateKeyboardPosition();
        }, 350);

        this.dispatchEvent(new CustomEvent('product-model-search:open'));
    }

    /* ========================================
       CLOSE MODAL / DRAWER
       (Cancel, X, Backdrop, Escape, Add — sab yahin aate hain)
    ======================================== */

    close() {

        this.isOpen = false;

        this.overlay.classList.remove('is-active');

        const selectedProducts = Array.from(this.selectedProducts.values());

        /** Display selected products in warranty table */
        this.displatTable(selectedProducts);

        /** ✅ Selected data ke saath function run */
        this.addDataInInput(selectedProducts);

        document.body.classList.remove('nkl__product__model__no-scroll');

        /** Reset only footer keyboard positioning */
        this.resetKeyboardFooter();
    }

    /* ========================================
       ADD DATA IN INPUT
       Cancel / Add / Delete — teeno par run hota hai.
       selectedProducts = [{ sku, name, warranty_year, ... }]
       Apni logic ke hisaab se body change kar lena.
    ======================================== */

    // addDataInInput(selectedProducts = []) {

    //     const data = selectedProducts;
    //     const warrantyModel = document.getElementById('warrantyModel');
    //     const warrnatyYears = document.getElementById('warrantyYear');
    //     console.log(data);
    //     debugger;

    // }

    addDataInInput(selectedProducts = []) {

    const data = selectedProducts;

    const warrantyModel = document.getElementById('warrantyModel');
    const warrantyYears = document.getElementById('warrantyYear');

    if (warrantyModel) {
        warrantyModel.value = data
            .map(product => product.name || '')
            .join(' | ');
    }

    if (warrantyYears) {
        warrantyYears.value = data
            .map(product => product.warranty_year || '')
            .join(' | ');
    }
}

    /* ========================================
       MOBILE KEYBOARD POSITION
       Modal height change nahi hogi, sirf footer keyboard ke upar.
    ======================================== */

    updateKeyboardPosition() {

        if (
            !this.isOpen ||
            !this.modal ||
            !this.footer ||
            !window.visualViewport
        ) {
            return;
        }

        /** Desktop par kuch nahi karna */
        if (window.innerWidth > 767) {
            this.resetKeyboardFooter();
            return;
        }

        const viewport = window.visualViewport;
        const viewportHeight = viewport.height;
        const windowHeight = window.innerHeight;

        const keyboardHeight = Math.max(
            0,
            windowHeight - viewportHeight - viewport.offsetTop
        );

        this.keyboardHeight = keyboardHeight;

        if (keyboardHeight > 100) {

            /** KEYBOARD OPEN */
            this.footer.style.position = 'fixed';
            this.footer.style.left = '0';
            this.footer.style.right = '0';
            this.footer.style.bottom = `${keyboardHeight}px`;
            this.footer.style.width = '100%';
            this.footer.style.zIndex = '99999';
            this.footer.style.background = '#fff';

            this.footer.style.setProperty('--keyboard-height', `${keyboardHeight}px`);

            this.footer.classList.add('keyboard-visible');

        } else {

            /** KEYBOARD CLOSED */
            this.resetKeyboardFooter();
        }
    }

    /* ========================================
       RESET KEYBOARD FOOTER
    ======================================== */

    resetKeyboardFooter() {

        if (!this.footer) {
            return;
        }

        this.footer.style.removeProperty('position');
        this.footer.style.removeProperty('left');
        this.footer.style.removeProperty('right');
        this.footer.style.removeProperty('bottom');
        this.footer.style.removeProperty('width');
        this.footer.style.removeProperty('z-index');
        this.footer.style.removeProperty('background');
        this.footer.style.removeProperty('--keyboard-height');

        this.footer.classList.remove('keyboard-visible');

        this.keyboardHeight = 0;
    }

    /* ========================================
       CLEAR SEARCH
    ======================================== */

    clearSearch() {

        this.searchInput.value = '';
        this.searchTerm = '';
        this.currentPage = 0;

        this.updateClearButtonVisibility();
        this.renderProductList();

        this.searchInput.focus();

        setTimeout(() => {
            this.updateKeyboardPosition();
        }, 100);
    }

    /* ========================================
       CLEAR BUTTON VISIBILITY
    ======================================== */

    updateClearButtonVisibility() {

        if (!this.clearButton) {
            return;
        }

        const hasValue = this.searchInput.value.length > 0;

        this.clearButton.classList.toggle('is-visible', hasValue);
    }

    /* ========================================
       GET FILTERED PRODUCTS
    ======================================== */

    getFilteredProducts() {

        if (!this.searchTerm) {
            return [...this.productData];
        }

        return this.productData.filter(product => {

            const title = String(product.name || '').toLowerCase();
            const sku = String(product.sku || '').toLowerCase();
            const warranty = String(product.warranty_year || '').toLowerCase();

            return (
                title.includes(this.searchTerm) ||
                sku.includes(this.searchTerm) ||
                warranty.includes(this.searchTerm)
            );
        });
    }

    /* ========================================
       GET VISIBLE PRODUCTS
    ======================================== */

    getVisibleProducts() {
        return this.getFilteredProducts();
    }

    /* ========================================
       RENDER PRODUCT LIST
    ======================================== */

    renderProductList() {

        if (!this.productList) {
            return;
        }

        const products = this.getVisibleProducts();

        const visibleProducts = products.slice(
            0,
            (this.currentPage + 1) * this.batchSize
        );

        if (!visibleProducts.length) {

            this.productList.innerHTML = `
                <div class="nkl__product__model__empty">
                    No product models found.
                </div>
            `;

            return;
        }

        this.productList.innerHTML = visibleProducts
            .map(product => this.getProductHTML(product))
            .join('');

        this.updateSelectedState();
    }

    /* ========================================
       PRODUCT ROW HTML
    ======================================== */

    getProductHTML(product) {

        const isSelected = this.selectedProducts.has(product.sku);

        return `
            <label class="nkl__product__model__item ${isSelected ? 'is-selected' : ''}">

                <div class="nkl__product__model__item-left">

                    <input
                        type="checkbox"
                        class="nkl__product__model__checkbox"
                        data-sku="${this.escapeHTML(product.sku)}"
                        ${isSelected ? 'checked' : ''}
                    >

                    <span class="nkl__product__model__checkmark"></span>

                </div>

                <div class="nkl__product__model__item-content">

                    <div class="nkl__product__model__title">
                        ${this.escapeHTML(product.name || '')}
                    </div>

                    <div class="nkl__product__model__meta">
                        <span>
                            ${this.escapeHTML(product.warranty_year || '')}
                        </span>
                    </div>

                </div>

            </label>
        `;
    }

    /* ========================================
       LOAD NEXT BATCH
    ======================================== */

    loadNextBatch() {

        const products = this.getVisibleProducts();

        const maxPages = Math.ceil(products.length / this.batchSize);

        if (this.currentPage + 1 >= maxPages) {
            return;
        }

        if (this.isLoading) {
            return;
        }

        this.currentPage++;

        this.renderProductList();
    }

    /* ========================================
       UPDATE SELECTED STATE
    ======================================== */

    updateSelectedState() {

        const selectedProducts = Array.from(this.selectedProducts.values());

        const count = selectedProducts.length;

        /** Update count */
        this.selectedCount.textContent = count;

        /** Add button enable / disable */
        this.addButton.disabled = count === 0;

        /** SELECTED PRODUCT NAMES */
        if (!this.selectedProductsContainer) {
            return;
        }

        this.selectedProductsContainer.innerHTML = selectedProducts
            .map(product => `
                <div
                    class="nkl__product__model__selected-chip"
                    title="${this.escapeHTML(product.name || '')}"
                >
                    ${this.escapeHTML(product.name || '')}
                </div>
            `)
            .join('');

        /** Latest selected product ko view mein scroll karo */
        if (selectedProducts.length) {

            requestAnimationFrame(() => {
                this.selectedProductsContainer.scrollLeft =
                    this.selectedProductsContainer.scrollWidth;
            });
        }
    }

    /* ========================================
       ADD SELECTED PRODUCTS
    ======================================== */

    addSelectedProducts() {

        const selectedProducts = Array.from(this.selectedProducts.values());

        /** Dispatch event */
        this.dispatchEvent(
            new CustomEvent('product-model-search:selected', {
                bubbles: true,
                detail: {
                    products: selectedProducts
                }
            })
        );

        /** close() ke andar addDataInInput(selectedProducts) already chalta hai */
        this.close();
    }

    /* ========================================
       ESCAPE HTML
    ======================================== */

    escapeHTML(value) {

        return String(value)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;');
    }

    /* ========================================
       DISPLAY TABLE
    ======================================== */

    displatTable(value) {

        const selectedData = Array.isArray(value) ? value : [];

        const insertTable = document.querySelector('.nkl__warrant__tbody');

        if (!insertTable) {
            return;
        }

        /** Delete SVG */
        const deleteSvg = `
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M16 10V17C16 17.2652 15.8946 17.5196 15.7071 17.7071C15.5196 17.8946 15.2652 18 15 18H9C8.73478 18 8.48043 17.8946 8.29289 17.7071C8.10536 17.5196 8 17.2652 8 17V10H16ZM13.5 6L14.5 7H17V9H7V7H9.5L10.5 6H13.5Z" fill="#CC0C39"/>
            </svg>
        `;

        /** EMPTY STATE */
        if (selectedData.length === 0) {

            insertTable.innerHTML = `
                <tr class="nkl__warrant__tr">
                    <td class="nkl__warrant__td nkl__warrant__product-td empty">
                        Add Product to Proceed
                    </td>
                    <td class="nkl__warrant__td nkl__warrant__warranty-td"></td>
                    <td class="nkl__warrant__td nkl__warrant__action-td"></td>
                </tr>
            `;

            return;
        }

        /** RENDER SELECTED PRODUCTS */
        insertTable.innerHTML = selectedData
            .map(product => {

                const sku = this.escapeHTML(product.sku || '');
                const productName = this.escapeHTML(product.name || '');
                const warranty = this.escapeHTML(product.warranty_year || '');

                return `
                    <tr class="nkl__warrant__tr" data-sku="${sku}">

                        <td class="nkl__warrant__td nkl__warrant__product-td">
                           <span>${productName}</span>
                           <span class="year__mobile__show">${warranty}</span>
                        </td>

                        <td class="nkl__warrant__td nkl__warrant__warranty-td">
                            ${warranty}
                        </td>

                        <td class="nkl__warrant__td nkl__warrant__action-td">
                            <button
                                type="button"
                                class="nkl__warrant__delete"
                                data-sku="${sku}"
                                aria-label="Delete ${productName}"
                            >
                                ${deleteSvg}
                            </button>
                        </td>

                    </tr>
                `;
            })
            .join('');

        /** DELETE BUTTON EVENTS */
        insertTable
            .querySelectorAll('.nkl__warrant__delete')
            .forEach(button => {

                button.addEventListener('click', () => {

                    const sku = button.dataset.sku;

                    this.openDeleteConfirmation(sku);
                });
            });
    }

    /* ========================================
       DELETE CONFIRMATION
    ======================================== */

    openDeleteConfirmation(sku) {

        const product = this.selectedProducts.get(sku);

        if (!product) {
            return;
        }

        /** Remove existing popup */
        const existingPopup = document.querySelector('.nkl__warrant__delete-popup');

        if (existingPopup) {
            existingPopup.remove();
        }

        const productName = product.name || 'this product';

        /** Create popup */
        const popup = document.createElement('div');

        popup.className = 'nkl__warrant__delete-popup';

        popup.innerHTML = `
            <div class="nkl__warrant__delete-overlay"></div>

            <div class="nkl__warrant__delete-modal" role="dialog" aria-modal="true">

                <div class="nkl__warrant__delete-content">

                    <h3 class="nkl__warrant__delete-title">
                        Delete Product?
                    </h3>

                    <p class="nkl__warrant__delete-message">
                        Do you want to delete
                        <strong>${this.escapeHTML(productName)}</strong>?
                    </p>

                </div>

                <div class="nkl__warrant__delete-actions">

                    <button type="button" class="nkl__warrant__delete-cancel">
                        Cancel
                    </button>

                    <button type="button" class="nkl__warrant__delete-confirm">
                        Delete
                    </button>

                </div>

            </div>
        `;

        document.body.appendChild(popup);

        /** ESCAPE handler (cleanup ke saath) */
        const escHandler = (event) => {
            if (event.key === 'Escape') {
                closePopup();
            }
        };

        const closePopup = () => {
            popup.remove();
            document.removeEventListener('keydown', escHandler);
        };

        /** CANCEL */
        popup
            .querySelector('.nkl__warrant__delete-cancel')
            .addEventListener('click', closePopup);

        /** OVERLAY CLICK */
        popup
            .querySelector('.nkl__warrant__delete-overlay')
            .addEventListener('click', closePopup);

        /** DELETE */
        popup
            .querySelector('.nkl__warrant__delete-confirm')
            .addEventListener('click', () => {

                this.deleteSelectedProduct(sku);

                closePopup();
            });

        document.addEventListener('keydown', escHandler);
    }

    /* ========================================
       DELETE SELECTED PRODUCT
    ======================================== */

    deleteSelectedProduct(sku) {

        if (!sku) {
            return;
        }

        /** Remove from selected Map */
        this.selectedProducts.delete(sku);

        /** Uncheck checkbox */
        if (this.productList) {

            const checkbox = this.productList.querySelector(
                `.nkl__product__model__checkbox[data-sku="${CSS.escape(sku)}"]`
            );

            if (checkbox) {

                checkbox.checked = false;

                const item = checkbox.closest('.nkl__product__model__item');

                if (item) {
                    item.classList.remove('is-selected');
                }
            }
        }

        /** Update selected state */
        this.updateSelectedState();

        /** Delete ke baad bacha hua updated selected array */
        const selectedProducts = Array.from(this.selectedProducts.values());

        /** Update warranty table */
        this.displatTable(selectedProducts);

        /** ✅ Delete ke baad updated data ke saath function run */
        this.addDataInInput(selectedProducts);
    }

}

customElements.define('product-model-search', ProductSearchAndSelect);